import {
  useQuery,
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { notificationService } from "../services/notificationService";
import { PAGINATION } from "../../../config/apiEndpoints";

const QUERY_KEYS = {
  list: (params) => ["notifications", "list", params],
  unreadCount: () => ["notifications", "unreadCount"],
};

export const useNotificationFeed = (params = {}) => {
  return useInfiniteQuery({
    queryKey: QUERY_KEYS.list(params),
    queryFn: ({ pageParam = 1 }) =>
      notificationService.getNotifications({
        page: pageParam,
        limit: PAGINATION.NOTIFICATIONS_PER_PAGE,
      }),
    getNextPageParam: (lastPage) => {
      const { page, totalPages } = lastPage ?? {};
      return page < totalPages ? page + 1 : undefined;
    },
    staleTime: 30 * 1000,
    refetchInterval: 60 * 1000, // poll every 60s
    refetchIntervalInBackground: false,
  });
};

export const useUnreadNotificationCount = () => {
  return useQuery({
    queryKey: QUERY_KEYS.unreadCount(),
    queryFn: notificationService.getUnreadCount,
    staleTime: 1000 * 20,
    refetchInterval: 1000 * 30, // poll every 30s
    refetchIntervalInBackground: false,
    select: (data) => data?.unreadCount ?? 0,
  });
};

export const useMarkNotificationRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationService.markRead(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useMarkAllNotificationsRead = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: notificationService.markAllRead,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useDeleteNotification = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => notificationService.deleteNotification(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["notifications"] });
    },
  });
};

export const useNotificationPreferences = () =>
  useQuery({
    queryKey: ["notifications", "preferences"],
    queryFn: notificationService.getPreferences,
    staleTime: 1000 * 60 * 5,
    select: (res) => res?.data ?? res ?? [],
  });

export const useUpdateNotificationPreferences = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (preferences) =>
      notificationService.updatePreferences(preferences),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", "preferences"],
      });
    },
  });
};
